import { useState, useMemo } from "react";
import { useV1ProductionProductionOrdersList, v1ProductionProductionOrdersList } from "@/api/generated/v1/v1";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";
import { DataTableToolbar } from "@/shared/components/DataTableToolbar";
import { exportToExcel, exportToPDF } from "@/shared/utils/export";
import { PageBanner } from "@/shared/components/PageBanner";

export function ProductionOrderListPage() {
  const navigate = useNavigate();
  const canCreate = usePermission("production.manage");

  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("all");

  // Fetch full list to compute dashboard KPI summary stats
  const { data: allData, isLoading: isAllLoading } = useV1ProductionProductionOrdersList({ page_size: 1000 } as any);
  const allOrders = allData?.results || [];

  // Query params for table listing
  const queryParams = {
    page: 1,
    page_size: 1000,
    ...(searchTerm && { search: searchTerm }),
    ...activeFilters,
  };

  const { data, isLoading, isError, error } = useV1ProductionProductionOrdersList(queryParams as any);

  // Statistics calculation across full dataset
  const totalOrdersCount = allOrders.length;
  const inProgressCount = useMemo(() => allOrders.filter((o: any) => o.status === "in_progress").length, [allOrders]);
  const urgentHighCount = useMemo(
    () => allOrders.filter((o: any) => o.priority === "urgent" || o.priority === "high").length,
    [allOrders]
  );
  const releasedCount = useMemo(() => allOrders.filter((o: any) => o.status === "released").length, [allOrders]);
  const draftCount = useMemo(() => allOrders.filter((o: any) => o.status === "draft").length, [allOrders]);
  const completedCount = useMemo(() => allOrders.filter((o: any) => o.status === "completed").length, [allOrders]);
  const onHoldCount = useMemo(() => allOrders.filter((o: any) => o.status === "on_hold").length, [allOrders]);
  const draftOnHoldCount = draftCount + onHoldCount;

  // Filter handlers
  const handleFilterChange = (name: string, value: string) => {
    setActiveFilters((prev) => {
      const next = { ...prev };
      if (!value) {
        delete next[name];
      } else {
        next[name] = value;
      }
      return next;
    });
  };

  const handleQuickTabSelect = (tabKey: string) => {
    setActiveTab(tabKey);
    if (tabKey === "all") {
      setActiveFilters({});
    } else if (tabKey === "in_progress") {
      setActiveFilters({ status: "in_progress" });
    } else if (tabKey === "urgent") {
      setActiveFilters({ priority: "urgent" });
    } else if (tabKey === "released") {
      setActiveFilters({ status: "released" });
    } else if (tabKey === "draft") {
      setActiveFilters({ status: "draft" });
    } else if (tabKey === "completed") {
      setActiveFilters({ status: "completed" });
    } else if (tabKey === "on_hold") {
      setActiveFilters({ status: "on_hold" });
    }
  };

  const fetchExportData = async () => {
    try {
      setIsExporting(true);
      const res = await v1ProductionProductionOrdersList({
        export: "true",
        ...(searchTerm && { search: searchTerm }),
        ...activeFilters,
      } as any);
      if ((res as any).truncated) {
        alert("تعداد رکوردها بیش از ۱۰,۰۰۰ مورد است و اکسپورت محدود شد.");
      }
      return (res as any).results || res;
    } catch (err) {
      alert("خطا در دریافت اطلاعات برای خروجی");
      throw err;
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    { header: "شماره سفارش", accessor: (row: any) => row.order_number },
    { header: "موعد تحویل", accessor: (row: any) => row.due_date },
    { header: "اولویت", accessor: (row: any) => row.priority },
    { header: "وضعیت", accessor: (row: any) => row.status },
    { header: "تعداد برنامه‌ریزی‌شده", accessor: (row: any) => row.planned_quantity },
  ];

  const handleExportExcel = async () => {
    const rawData = await fetchExportData();
    await exportToExcel(rawData, columns, "production_orders_export");
  };

  const handleExportPDF = async () => {
    const rawData = await fetchExportData();
    await exportToPDF(rawData, columns, "production_orders_export");
  };

  // Helper render functions for status & priority badges
  const renderStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; bg: string; color: string; border: string }> = {
      in_progress: {
        label: "در حال انجام",
        bg: "rgba(59, 130, 246, 0.12)",
        color: "#2563EB",
        border: "rgba(59, 130, 246, 0.3)",
      },
      released: {
        label: "آزادشده",
        bg: "rgba(16, 185, 129, 0.12)",
        color: "#059669",
        border: "rgba(16, 185, 129, 0.3)",
      },
      draft: {
        label: "پیش‌نویس",
        bg: "rgba(245, 158, 11, 0.12)",
        color: "#D97706",
        border: "rgba(245, 158, 11, 0.3)",
      },
      completed: {
        label: "تکمیل‌شده",
        bg: "rgba(0, 114, 112, 0.12)",
        color: "#007270",
        border: "rgba(0, 114, 112, 0.3)",
      },
      on_hold: {
        label: "متوقف",
        bg: "rgba(239, 68, 68, 0.12)",
        color: "#DC2626",
        border: "rgba(239, 68, 68, 0.3)",
      },
      cancelled: {
        label: "لغوشده",
        bg: "rgba(107, 114, 128, 0.12)",
        color: "#4B5563",
        border: "rgba(107, 114, 128, 0.3)",
      },
    };

    const cfg = statusConfig[status] || {
      label: status || "-",
      bg: "rgba(0, 114, 112, 0.1)",
      color: "#007270",
      border: "rgba(0, 114, 112, 0.2)",
    };

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.35rem",
          padding: "0.3rem 0.65rem",
          borderRadius: "8px",
          fontSize: "0.82rem",
          fontWeight: "700",
          background: cfg.bg,
          color: cfg.color,
          border: `1px solid ${cfg.border}`,
        }}
      >
        <span
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            background: cfg.color,
          }}
        />
        {cfg.label}
      </span>
    );
  };

  const renderPriorityBadge = (priority: string) => {
    const priorityConfig: Record<string, { label: string; icon: string; bg: string; color: string; border: string }> = {
      urgent: {
        label: "فوری",
        icon: "🚨",
        bg: "rgba(239, 68, 68, 0.12)",
        color: "#DC2626",
        border: "rgba(239, 68, 68, 0.3)",
      },
      high: {
        label: "بالا",
        icon: "⚡",
        bg: "rgba(245, 158, 11, 0.12)",
        color: "#D97706",
        border: "rgba(245, 158, 11, 0.3)",
      },
      normal: {
        label: "عادی",
        icon: "🔹",
        bg: "rgba(0, 114, 112, 0.12)",
        color: "#007270",
        border: "rgba(0, 114, 112, 0.3)",
      },
      low: {
        label: "پایین",
        icon: "⚪",
        bg: "rgba(107, 114, 128, 0.12)",
        color: "#6B7280",
        border: "rgba(107, 114, 128, 0.3)",
      },
    };

    const cfg = priorityConfig[priority] || {
      label: priority || "-",
      icon: "•",
      bg: "rgba(107, 114, 128, 0.1)",
      color: "#4B5563",
      border: "rgba(107, 114, 128, 0.2)",
    };

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.35rem",
          padding: "0.25rem 0.6rem",
          borderRadius: "8px",
          fontSize: "0.82rem",
          fontWeight: "700",
          background: cfg.bg,
          color: cfg.color,
          border: `1px solid ${cfg.border}`,
        }}
      >
        <span>{cfg.icon}</span>
        <span>{cfg.label}</span>
      </span>
    );
  };

  const displayedOrders = data?.results || [];

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <style>{`
        .kpi-card {
          padding: 0.75rem 1rem;
          min-height: 110px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 16px;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 1rem;
          position: relative;
          overflow: hidden;
        }
        .kpi-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.12);
          border-color: rgba(0, 114, 112, 0.4);
        }
        .quick-tab-pill {
          padding: 0.5rem 1.1rem;
          border-radius: 12px;
          font-weight: 700;
          font-size: 0.85rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
          border: 1px solid transparent;
          white-space: nowrap;
        }
      `}</style>

      {/* 1. STANDARDIZED PAGE BANNER (DASHBOARD HEADER MATCH) */}
      <PageBanner
        title="مدیریت سفارشات تولید"
        subtitle="مشاهده، فیلتر و پیگیری کلیه سفارشات و کارهای تولیدی کارخانه"
        icon="📋"
        helpLink="/help/production/orders"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate("/production")}
            style={{
              padding: "0.6rem 1.1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.9rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>←</span>
            <span>برگشت</span>
          </button>

          {canCreate && (
            <button
              onClick={() => navigate("/production/orders/create")}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: "#ffffff",
                color: "#007270",
                fontWeight: 700,
                fontSize: "0.9rem",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>➕</span>
              <span>ایجاد سفارش</span>
            </button>
          )}
        </div>
      </PageBanner>

      {/* 2. DASHBOARD KPI SUMMARY CARDS GRID (MATCHING IMAGE 1 & DASHBOARD STYLE) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.25rem",
        }}
      >
        {/* KPI 1: IN PROGRESS / ACTIVE ORDERS */}
        <div
          className="brand-card kpi-card"
          onClick={() => handleQuickTabSelect(activeTab === "in_progress" ? "all" : "in_progress")}
          style={{
            borderColor: activeTab === "in_progress" ? "#007270" : undefined,
            background: activeTab === "in_progress" ? "rgba(0, 114, 112, 0.04)" : undefined,
          }}
        >
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(0,114,112,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ⚙️
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              سفارشات در حال انجام
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.3rem", fontWeight: "800", color: "#007270" }}>
                {isAllLoading ? "..." : inProgressCount}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#10B981", background: "rgba(16,185,129,0.1)", padding: "0.15rem 0.5rem", borderRadius: "6px" }}>
                زنده
              </span>
            </div>
          </div>
        </div>

        {/* KPI 2: URGENT & HIGH PRIORITY ORDERS */}
        <div
          className="brand-card kpi-card"
          onClick={() => handleQuickTabSelect(activeTab === "urgent" ? "all" : "urgent")}
          style={{
            borderColor: activeTab === "urgent" ? "#EF4444" : undefined,
            background: activeTab === "urgent" ? "rgba(239, 68, 68, 0.04)" : undefined,
          }}
        >
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(239,68,68,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ⚠️
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              اولویت‌های فوری و بالا
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.3rem", fontWeight: "800", color: urgentHighCount > 0 ? "#EF4444" : "#10B981" }}>
                {isAllLoading ? "..." : urgentHighCount}
              </span>
              <span
                style={{
                  fontSize: "0.8rem",
                  fontWeight: "600",
                  color: urgentHighCount > 0 ? "#EF4444" : "#10B981",
                  background: urgentHighCount > 0 ? "rgba(239, 68, 68, 0.1)" : "rgba(16, 185, 129, 0.1)",
                  padding: "0.15rem 0.5rem",
                  borderRadius: "6px",
                }}
              >
                {urgentHighCount > 0 ? "نیازمند پیگیری" : "عادی"}
              </span>
            </div>
          </div>
        </div>

        {/* KPI 3: RELEASED ORDERS */}
        <div
          className="brand-card kpi-card"
          onClick={() => handleQuickTabSelect(activeTab === "released" ? "all" : "released")}
          style={{
            borderColor: activeTab === "released" ? "#F59E0B" : undefined,
            background: activeTab === "released" ? "rgba(245, 158, 11, 0.04)" : undefined,
          }}
        >
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(245,158,11,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            🔓
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              سفارشات آزادشده
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.3rem", fontWeight: "800", color: "#D4AF37" }}>
                {isAllLoading ? "..." : releasedCount}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#F59E0B", background: "rgba(245,158,11,0.1)", padding: "0.15rem 0.5rem", borderRadius: "6px" }}>
                آماده خط تولید
              </span>
            </div>
          </div>
        </div>

        {/* KPI 4: DRAFT & ON HOLD ORDERS */}
        <div
          className="brand-card kpi-card"
          onClick={() => handleQuickTabSelect(activeTab === "draft" ? "all" : "draft")}
          style={{
            borderColor: activeTab === "draft" || activeTab === "on_hold" ? "#3B82F6" : undefined,
            background: activeTab === "draft" || activeTab === "on_hold" ? "rgba(59, 130, 246, 0.04)" : undefined,
          }}
        >
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(59,130,246,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ⏳
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              پیش‌نویس و متوقف
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.3rem", fontWeight: "800", color: "#3B82F6" }}>
                {isAllLoading ? "..." : draftOnHoldCount}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#3B82F6", background: "rgba(59,130,246,0.1)", padding: "0.15rem 0.5rem", borderRadius: "6px" }}>
                در انتظار
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 3. QUICK FILTER TABS BAR (MATCHING IMAGE 1 PILL FILTER BUTTONS) */}
      <div
        className="brand-card-extand"
        style={{
          padding: "0.85rem 1.25rem",
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          overflowX: "auto",
        }}
      >
        <button
          onClick={() => handleQuickTabSelect("all")}
          className="quick-tab-pill"
          style={{
            background: activeTab === "all" ? "#007270" : "rgba(0, 114, 112, 0.08)",
            color: activeTab === "all" ? "#ffffff" : "#007270",
            border: activeTab === "all" ? "1px solid #007270" : "1px solid rgba(0, 114, 112, 0.2)",
          }}
        >
          <span>همه سفارشات ({totalOrdersCount})</span>
        </button>

        <button
          onClick={() => handleQuickTabSelect("in_progress")}
          className="quick-tab-pill"
          style={{
            background: activeTab === "in_progress" ? "#007270" : "rgba(0, 114, 112, 0.08)",
            color: activeTab === "in_progress" ? "#ffffff" : "#007270",
            border: activeTab === "in_progress" ? "1px solid #007270" : "1px solid rgba(0, 114, 112, 0.2)",
          }}
        >
          <span>⚡ در حال انجام ({inProgressCount})</span>
        </button>

        <button
          onClick={() => handleQuickTabSelect("urgent")}
          className="quick-tab-pill"
          style={{
            background: activeTab === "urgent" ? "#007270" : "rgba(0, 114, 112, 0.08)",
            color: activeTab === "urgent" ? "#ffffff" : "#007270",
            border: activeTab === "urgent" ? "1px solid #007270" : "1px solid rgba(0, 114, 112, 0.2)",
          }}
        >
          <span>🚨 فوری و اضطراری ({urgentHighCount})</span>
        </button>

        <button
          onClick={() => handleQuickTabSelect("released")}
          className="quick-tab-pill"
          style={{
            background: activeTab === "released" ? "#007270" : "rgba(0, 114, 112, 0.08)",
            color: activeTab === "released" ? "#ffffff" : "#007270",
            border: activeTab === "released" ? "1px solid #007270" : "1px solid rgba(0, 114, 112, 0.2)",
          }}
        >
          <span>🔓 آزادشده ({releasedCount})</span>
        </button>

        <button
          onClick={() => handleQuickTabSelect("draft")}
          className="quick-tab-pill"
          style={{
            background: activeTab === "draft" ? "#007270" : "rgba(0, 114, 112, 0.08)",
            color: activeTab === "draft" ? "#ffffff" : "#007270",
            border: activeTab === "draft" ? "1px solid #007270" : "1px solid rgba(0, 114, 112, 0.2)",
          }}
        >
          <span>📝 پیش‌نویس ({draftCount})</span>
        </button>

        <button
          onClick={() => handleQuickTabSelect("completed")}
          className="quick-tab-pill"
          style={{
            background: activeTab === "completed" ? "#007270" : "rgba(0, 114, 112, 0.08)",
            color: activeTab === "completed" ? "#ffffff" : "#007270",
            border: activeTab === "completed" ? "1px solid #007270" : "1px solid rgba(0, 114, 112, 0.2)",
          }}
        >
          <span>✅ تکمیل‌شده ({completedCount})</span>
        </button>

        <button
          onClick={() => handleQuickTabSelect("on_hold")}
          className="quick-tab-pill"
          style={{
            background: activeTab === "on_hold" ? "#007270" : "rgba(0, 114, 112, 0.08)",
            color: activeTab === "on_hold" ? "#ffffff" : "#007270",
            border: activeTab === "on_hold" ? "1px solid #007270" : "1px solid rgba(0, 114, 112, 0.2)",
          }}
        >
          <span>⏸️ متوقف ({onHoldCount})</span>
        </button>
      </div>

      {/* 4. MAIN DATA TABLE SECTION */}
      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        <DataTableToolbar
          onSearch={setSearchTerm}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onExportExcel={handleExportExcel}
          onExportPDF={handleExportPDF}
          isExporting={isExporting}
          dateRangeFilters={[{ field: "created_at", label: "تاریخ ایجاد" }]}
          filters={[
            {
              name: "status",
              label: "وضعیت",
              options: [
                { label: "پیش‌نویس", value: "draft" },
                { label: "آزادشده", value: "released" },
                { label: "در حال انجام", value: "in_progress" },
                { label: "تکمیل‌شده", value: "completed" },
                { label: "لغوشده", value: "cancelled" },
                { label: "متوقف", value: "on_hold" },
              ],
            },
            {
              name: "priority",
              label: "اولویت",
              options: [
                { label: "پایین", value: "low" },
                { label: "عادی", value: "normal" },
                { label: "بالا", value: "high" },
                { label: "فوری", value: "urgent" },
              ],
            },
          ]}
        />

        {isLoading && (
          <div style={{ textAlign: "center", padding: "2.5rem" }}>
            <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری سفارشات تولید...</p>
          </div>
        )}

        {isError && (
          <div
            style={{
              padding: "1rem",
              marginBottom: "1rem",
              background: "rgba(239, 68, 68, 0.1)",
              borderRight: "4px solid #EF4444",
              borderRadius: "8px",
            }}
          >
            <p style={{ color: "#EF4444" }}>خطا در دریافت اطلاعات: {(error as Error)?.message}</p>
          </div>
        )}

        {!isLoading && !isError && (
          <div className="table-container" style={{ overflowX: "auto", marginTop: "1rem" }}>
            <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--color-border)", color: "#007270" }}>
                  <th style={{ padding: "0.85rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>شماره سفارش</th>
                  <th style={{ padding: "0.85rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>بام / روتینگ</th>
                  <th style={{ padding: "0.85rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>تعداد برنامه‌ریزی‌شده</th>
                  <th style={{ padding: "0.85rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>وضعیت</th>
                  <th style={{ padding: "0.85rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>اولویت</th>
                  <th style={{ padding: "0.85rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>تاریخ ایجاد</th>
                  <th style={{ padding: "0.85rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {displayedOrders.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: "2.5rem", color: "var(--color-text-muted)" }}>
                      هیچ سفارشی مطابق با فیلترهای انتخابی یافت نشد.
                    </td>
                  </tr>
                ) : (
                  displayedOrders.map((order: any) => (
                    <tr
                      key={order.id}
                      style={{ borderBottom: "1px solid var(--color-border)", transition: "background 0.2s" }}
                      className="hover:bg-black/5"
                    >
                      <td style={{ padding: "0.85rem 1rem", fontWeight: "700", color: "#007270" }}>
                        {order.order_number}
                      </td>
                      <td style={{ padding: "0.85rem 1rem" }}>
                        <span style={{ fontSize: "0.9rem", fontWeight: "600", color: "var(--color-text-main)" }}>
                          {order.bom_family_display?.name || order.routing_display?.name || "-"}
                        </span>
                      </td>
                      <td style={{ padding: "0.85rem 1rem", fontWeight: "700" }}>
                        {Number(order.planned_quantity || 0).toLocaleString("fa-IR")}
                      </td>
                      <td style={{ padding: "0.85rem 1rem" }}>{renderStatusBadge(order.status)}</td>
                      <td style={{ padding: "0.85rem 1rem" }}>{renderPriorityBadge(order.priority)}</td>
                      <td style={{ padding: "0.85rem 1rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                        {order.created_at ? new Date(order.created_at).toLocaleDateString("fa-IR") : "-"}
                      </td>
                      <td style={{ padding: "0.85rem 1rem" }}>
                        <button
                          onClick={() => navigate(`/production/orders/${order.id}`)}
                          style={{
                            padding: "0.4rem 0.8rem",
                            borderRadius: "8px",
                            fontSize: "0.82rem",
                            fontWeight: "700",
                            border: "1px solid #007270",
                            background: "transparent",
                            color: "#007270",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <span>جزئیات</span>
                          <span>🔍</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
